import React from "react";
import { Link } from "react-router-dom";
import { HiArrowNarrowLeft } from "react-icons/hi";
import logo from "@/assets/logo.png";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors">
            <HiArrowNarrowLeft className="w-5 h-5" />
            Retour
          </Link>
          <img src={logo} alt="Logo" className="h-8" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-16">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Politique de confidentialité</h1>
        <p className="text-sm text-gray-400 mb-8">Dernière mise à jour : 15 août 2026</p>

        <div className="space-y-8 text-sm text-gray-600 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">1. Qui traite vos données ?</h2>
            <p>
              Ce service de pré-enregistrement en ligne est proposé par le laboratoire de biologie médicale que vous sélectionnez (le « Laboratoire »). Le Laboratoire est
              le <span className="font-semibold text-gray-900">responsable du traitement</span> de vos données personnelles, au sens du Règlement général sur la
              protection des données (RGPD).
            </p>
            <p className="mt-2">
              La plateforme est éditée et opérée par QUERCO, SARL au capital de 500 €, 2 rue du Neuberg, 68610 Lautenbach, France, RCS Colmar 891 328 619 (« Querco »), qui
              agit en qualité de <span className="font-semibold text-gray-900">sous-traitant</span> du Laboratoire, dans le cadre d'un contrat conforme à l'article 28 du
              RGPD.
            </p>
            <p className="mt-2">
              Pour toute question relative à vos données, vous pouvez contacter le Laboratoire directement ou écrire à{" "}
              <a href="mailto:privacy@querco.co" className="text-primary hover:underline">
                privacy@querco.co
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">2. Quelles données sont collectées ?</h2>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                <span className="font-medium text-gray-900">Identité et affiliation :</span> nom, prénom, nom de naissance, date et lieu de naissance, sexe, numéro de
                sécurité sociale (NIR), adresse postale ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Coordonnées :</span> adresse e-mail, numéro de téléphone ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Documents et données de santé :</span> ordonnance transmise (photo ou PDF) et son contenu, réponses au
                questionnaire préalable aux prélèvements, documents joints à certaines questions ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Données de connexion et de sécurité :</span> adresse IP, caractéristiques du navigateur, identifiants
                techniques de session, journaux d'accès, codes de vérification à usage unique ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Données d'utilisation :</span> événements de navigation dans le service (pages, étapes complétées), mesurés à
                des fins statistiques et d'amélioration.
              </li>
            </ul>
            <p className="mt-2">
              Lorsque vous vérifiez votre identité, certaines de ces informations peuvent être préremplies à partir du dossier administratif déjà détenu par le
              Laboratoire dans son système d'information. Si vous effectuez l'enregistrement pour le compte d'un tiers majeur avec son autorisation, vous vous engagez à
              porter la présente politique à sa connaissance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">3. Pourquoi et sur quelles bases légales ?</h2>
            <ul className="list-disc pl-5 mt-2 space-y-2">
              <li>
                <span className="font-medium text-gray-900">Préparer votre admission au Laboratoire</span> (constitution du dossier administratif, lecture de
                l'ordonnance, questionnaire, consignes, signalement de présence) : mesures précontractuelles et prise en charge sanitaire. Les données de santé sont
                traitées sur le fondement de l'article 9.2.h du RGPD, sous la responsabilité de professionnels soumis au secret médical ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Vérifier votre identité (identitovigilance)</span>, y compris l'utilisation de votre numéro de sécurité
                sociale dans le cadre de l'Identité Nationale de Santé : obligation légale du Laboratoire ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Vous authentifier et prévenir la fraude</span> (codes à usage unique par e-mail ou SMS, protection des
                sessions) : intérêt légitime à sécuriser le service ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Vous envoyer les messages liés à votre parcours</span> (code de vérification, confirmation, signalement de
                présence) : exécution du service. Aucune prospection commerciale n'est réalisée à partir de vos données ;
              </li>
              <li>
                <span className="font-medium text-gray-900">Mesurer l'utilisation du service et corriger les dysfonctionnements :</span> intérêt légitime à améliorer et
                fiabiliser le service ; les traceurs de mesure d'audience ne sont déposés qu'avec votre consentement, recueilli via le bandeau cookies et modifiable à
                tout moment (voir la{" "}
                <Link to="/cookies" className="text-primary hover:underline">
                  politique cookies
                </Link>
                ).
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">4. Qui accède à vos données ?</h2>
            <p>
              Vos données sont destinées au personnel habilité du Laboratoire, soumis au secret professionnel, et au personnel strictement habilité de Querco pour
              l'exploitation et le support de la plateforme.
            </p>
            <p className="mt-2">Querco fait appel aux catégories de prestataires techniques suivantes :</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>un hébergeur de données certifié Hébergeur de Données de Santé (HDS), situé dans l'Union européenne ;</li>
              <li>un service de reconnaissance automatique de texte pour la lecture des ordonnances, exécuté dans l'Union européenne ;</li>
              <li>des services d'envoi d'e-mails et de SMS ;</li>
              <li>un outil de supervision des erreurs techniques, dont les données sont ingérées dans l'Union européenne ;</li>
              <li>
                un outil de mesure d'audience hébergé dans l'Union européenne, activé uniquement avec votre consentement (voir la{" "}
                <Link to="/cookies" className="text-primary hover:underline">
                  politique cookies
                </Link>
                ) ;
              </li>
              <li>
                le service public français de géocodage, qui reçoit uniquement le texte saisi dans les champs d'adresse et de lieu de naissance pour vous proposer des
                suggestions.
              </li>
            </ul>
            <p className="mt-2">
              La liste détaillée de ces prestataires peut être obtenue sur demande à{" "}
              <a href="mailto:privacy@querco.co" className="text-primary hover:underline">
                privacy@querco.co
              </a>
              . Vos données ne sont ni vendues, ni louées, ni utilisées à des fins publicitaires.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">5. Vos données quittent-elles l'Union européenne ?</h2>
            <p>
              Vos données sont hébergées dans l'Union européenne. Certains de ces prestataires sont des sociétés de droit américain ; lorsqu'un transfert hors de l'Union
              européenne est susceptible d'intervenir, il est encadré par des garanties appropriées : clauses contractuelles types de la Commission européenne ou
              certification au cadre de protection des données UE–États-Unis (Data Privacy Framework). Une copie de ces garanties peut être demandée à{" "}
              <a href="mailto:privacy@querco.co" className="text-primary hover:underline">
                privacy@querco.co
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">6. Combien de temps vos données sont-elles conservées ?</h2>
            <p>
              Les jetons techniques liés à votre connexion sont conservés de quelques heures à 45 jours (détail dans la{" "}
              <Link to="/cookies" className="text-primary hover:underline">
                politique cookies
              </Link>
              ). Vos ordonnances renouvelables sont conservées le temps de leur validité afin de pouvoir être réutilisées lors d'une prochaine visite.
            </p>
            <p className="mt-2">
              Une fois transmises au Laboratoire, les données de votre dossier d'admission intègrent votre dossier de biologie médicale, conservé par le Laboratoire
              conformément à ses obligations légales. Si vous abandonnez votre enregistrement avant sa finalisation, la demande en cours est supprimée. Les journaux
              techniques et données de mesure d'audience sont conservés pour des durées limitées, proportionnées à leur finalité (au maximum 13 mois pour les traceurs).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">7. Comment vos données sont-elles protégées ?</h2>
            <p>
              Les échanges sont chiffrés (TLS), les jetons de session sont chiffrés et à durée de vie courte, les accès sont limités aux personnels habilités et
              journalisés, et les données de chaque Laboratoire sont cloisonnées. Les données sont hébergées dans l'Union européenne sur des infrastructures certifiées
              Hébergeur de Données de Santé (HDS). L'accès à vos informations préremplies nécessite une vérification par code à usage unique envoyé sur votre e-mail ou
              votre téléphone.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">8. Quels sont vos droits ?</h2>
            <p>
              Vous disposez des droits d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité sur vos données, ainsi que du droit de
              définir des directives relatives à leur sort après votre décès.
            </p>
            <p className="mt-2">
              Vous pouvez exercer ces droits auprès du Laboratoire que vous avez sélectionné, ou en écrivant à{" "}
              <a href="mailto:privacy@querco.co" className="text-primary hover:underline">
                privacy@querco.co
              </a>{" "}
              — votre demande sera alors transmise au Laboratoire concerné. Un justificatif d'identité pourra vous être demandé en cas de doute raisonnable.
            </p>
            <p className="mt-2">
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser une réclamation à la CNIL (
              <a href="https://www.cnil.fr" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                www.cnil.fr
              </a>
              ).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">9. Mineurs</h2>
            <p>Le service est réservé aux personnes majeures. Les admissions de personnes mineures sont réalisées directement à l'accueil du Laboratoire.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-2">10. Évolution de cette politique</h2>
            <p>
              Cette politique peut être mise à jour pour refléter les évolutions du service ou de la réglementation. La version en vigueur est accessible en permanence
              sur cette page, et toute modification substantielle sera portée à votre connaissance par tout moyen approprié avant son entrée en vigueur.
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/terms" className="text-primary hover:underline">
            Conditions générales d'utilisation
          </Link>
          <Link to="/cookies" className="text-primary hover:underline">
            Politique cookies
          </Link>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
